import { getDeviceId } from "shared/util/auth/device-id";
import { getCurrentLanguage } from "shared/utility/lang";
import { markEnterDemoRoute } from "./start-flags";
import { getUtmParams } from "shared/util/utm";

let inFlight = null;

export const resetDemoSessionLock = () => {
  inFlight = null;
};

export const getUtmParamsFromSearch = (search = typeof window !== "undefined" ? window.location.search : "") =>
  getUtmParams(search);

/**
 * Creates a demo tenant and returns a logged-in session for it *without* touching the app's
 * auth state. `seed: false` opens an empty workspace for an operator's own spreadsheet;
 * the default is the seeded live demo used by Login and the sample tour.
 */
export const createDemoSession = async ({ sendRequest, utm, search, seed = true, source } = {}) => {
  const language = getCurrentLanguage();
  const deviceId = getDeviceId();
  const utmData = utm || getUtmParamsFromSearch(search);
  const body = {};
  if (utmData) body.utm = utmData;
  if (!seed) body.seed = false;
  if (source) body.source = source;

  // Visitors have no token, and sendRequest only adds the JSON content type alongside one —
  // without it the server never reads utm / seed. Send it explicitly.
  const created = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/clients/createdemoaccount`, "POST", JSON.stringify(body), {
    "Content-Type": "application/json",
  });

  const session = await sendRequest(
    `${process.env.REACT_APP_BACKEND_URL}/clients/login`,
    "POST",
    JSON.stringify({
      phonenumber: created.email,
      password: created.tempPassword,
      language,
      deviceId,
    }),
    { "Content-Type": "application/json" },
  );

  return { created, session };
};

/**
 * Creates a demo account and logs in exactly once while a request is in flight.
 * React Strict Mode remounts reuse the in-flight promise instead of opening
 * additional demo tenants. After the request settles, a later click can retry.
 */
export const createAndLoginDemoAccount = async ({ sendRequest, login, utm, search, seed = true, source } = {}) => {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const { session } = await createDemoSession({ sendRequest, utm, search, seed, source });
    markEnterDemoRoute();
    if (login) {
      await login(session.id, session.token);
    }
    return session;
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
};
